/**
 * Optimizer Form Component
 * Input form for warehouse optimization parameters and constraints
 */
import { useState } from 'react';
import { Warehouse } from 'lucide-react';
import type { OptimizationParams } from '../types';

interface OptimizerFormProps {
  onOptimize: (params: OptimizationParams) => void;
  isLoading: boolean;
}

export default function OptimizerForm({ onOptimize, isLoading }: OptimizerFormProps) {
  const [numWarehouses, setNumWarehouses] = useState(3);
  const [deliveryGoal, setDeliveryGoal] = useState<2 | 3>(2);
  const [specifyCarrier, setSpecifyCarrier] = useState(false);
  const [selectedCarriers, setSelectedCarriers] = useState<string[]>([]);
  const [requiredInput, setRequiredInput] = useState('');
  const [candidatesInput, setCandidatesInput] = useState('');
  const [excludedZip3sInput, setExcludedZip3sInput] = useState('');
  const [includedStatesInput, setIncludedStatesInput] = useState('');
  const [excludedStatesInput, setExcludedStatesInput] = useState('');

  const parseZip3List = (input: string): string[] => {
    return input
      .split(',')
      .map(z => z.trim())
      .filter(z => z.length === 3 && /^\d{3}$/.test(z));
  };

  const parseStateList = (input: string): string[] => {
    return input
      .split(',')
      .map(s => s.trim().toUpperCase())
      .filter(s => s.length === 2 && /^[A-Z]{2}$/.test(s));
  };

  // Check if form is valid
  const isFormValid = numWarehouses >= 1 && numWarehouses <= 20;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!isFormValid) {
      alert('Number of warehouses must be between 1 and 20');
      return;
    }

    const requiredZip3s = parseZip3List(requiredInput);
    const candidateZip3s = parseZip3List(candidatesInput);
    const excludedZip3s = parseZip3List(excludedZip3sInput);
    const includedStates = parseStateList(includedStatesInput);
    const excludedStates = parseStateList(excludedStatesInput);

    // Validate required count
    if (requiredZip3s.length > numWarehouses) {
      alert(`Required warehouses (${requiredZip3s.length}) cannot exceed total (${numWarehouses})`);
      return;
    }

    // Check for state conflicts
    if (includedStates.length > 0 && excludedStates.length > 0) {
      const overlap = includedStates.filter(s => excludedStates.includes(s));
      if (overlap.length > 0) {
        alert(`States cannot be both included and excluded: ${overlap.join(', ')}`);
        return;
      }
    }

    // Check for ZIP3 conflicts
    if (candidateZip3s.length > 0 && excludedZip3s.length > 0) {
      const overlap = candidateZip3s.filter(z => excludedZip3s.includes(z));
      if (overlap.length > 0) {
        alert(`ZIP3s cannot be both candidates and excluded: ${overlap.join(', ')}`);
        return;
      }
    }

    if (requiredZip3s.length > 0 && excludedZip3s.length > 0) {
      const overlap = requiredZip3s.filter(z => excludedZip3s.includes(z));
      if (overlap.length > 0) {
        alert(`ZIP3s cannot be both required and excluded: ${overlap.join(', ')}`);
        return;
      }
    }

    const params: OptimizationParams = {
      numWarehouses,
      algorithm: 'lazy_greedy',
      deliveryGoal,
      selectedCarriers: specifyCarrier && selectedCarriers.length > 0 ? selectedCarriers : undefined,
      requiredZip3s: requiredZip3s.length > 0 ? requiredZip3s : undefined,
      candidateZip3s: candidateZip3s.length > 0 ? candidateZip3s : undefined,
      excludedZip3s: excludedZip3s.length > 0 ? excludedZip3s : undefined,
      includedStates: includedStates.length > 0 ? includedStates : undefined,
      excludedStates: excludedStates.length > 0 ? excludedStates : undefined,
    };

    onOptimize(params);
  };

  const inputStyle = {
    width: '100%',
    padding: '0.5rem',
    border: '1px solid var(--border-color)',
    borderRadius: '4px',
    backgroundColor: 'var(--bg-secondary)',
    color: 'var(--text-primary)',
    fontSize: '0.875rem',
  };

  const narrowInputStyle = {
    ...inputStyle,
    width: '80px',
  };

  const mediumInputStyle = {
    ...inputStyle,
    width: '180px',
  };

  const wideInputStyle = {
    ...inputStyle,
    width: '215px',
  };

  const labelStyle = {
    display: 'block',
    fontSize: '0.875rem',
    fontWeight: 500,
    color: 'var(--text-primary)',
    marginBottom: '0.2rem',
  };

  const helperStyle = {
    fontSize: '0.75rem',
    color: 'var(--text-secondary)',
    marginTop: '0.1rem',
    marginLeft: '0.2rem',
  };

  const sectionHeaderStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '.5rem',
    fontSize: '0.875rem',
    fontWeight: 600,
    color: 'var(--text-secondary)',
    marginTop: '1rem',
    marginBottom: '0.5rem',
    paddingBottom: '.25rem',
    borderBottom: '1px solid var(--border-color)',
  };

  return (
    <form onSubmit={handleSubmit}>
      {/* Basic Settings */}
      <div style={{ marginBottom: '1rem' }}>
        <label style={labelStyle}>
          Delivery Target
        </label>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            type="button"
            onClick={() => setDeliveryGoal(2)}
            style={{
              flex: 1,
              padding: '0.5rem',
              border: '1px solid var(--border-color)',
              borderRadius: '4px',
              backgroundColor: deliveryGoal === 2 ? 'var(--primary)' : 'var(--bg-secondary)',
              color: deliveryGoal === 2 ? '#fff' : 'var(--text-primary)',
              fontWeight: deliveryGoal === 2 ? 600 : 400,
              cursor: 'pointer',
              fontSize: '0.875rem',
              transition: 'all 0.2s',
            }}
          >
            2 Days
          </button>
          <button
            type="button"
            onClick={() => setDeliveryGoal(3)}
            style={{
              flex: 1,
              padding: '0.5rem',
              border: '1px solid var(--border-color)',
              borderRadius: '4px',
              backgroundColor: deliveryGoal === 3 ? 'var(--primary)' : 'var(--bg-secondary)',
              color: deliveryGoal === 3 ? '#fff' : 'var(--text-primary)',
              fontWeight: deliveryGoal === 3 ? 600 : 400,
              cursor: 'pointer',
              fontSize: '0.875rem',
              transition: 'all 0.2s',
            }}
          >
            3 Days
          </button>
        </div>
      </div>

      <div style={{ marginBottom: '.5rem' }}>
        <label style={{ ...labelStyle, color: !isFormValid ? 'var(--error)' : 'var(--text-primary)' }}>
          Total Warehouses
        </label>
        <input
          type="number"
          min="1"
          max="20"
          value={numWarehouses === 0 ? '' : numWarehouses}
          onChange={(e) => {
            const val = e.target.value;
            setNumWarehouses(val === '' ? 0 : parseInt(val) || 0);
          }}
          style={narrowInputStyle}
          placeholder="1-20"
        />
        <div style={{ ...helperStyle, color: !isFormValid ? 'var(--error)' : 'var(--text-secondary)' }}>
          Number of warehouses to place
        </div>
      </div>

      {/* Carrier Selection */}
      <div style={{ marginBottom: '.5rem' }}>
        <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={specifyCarrier}
            onChange={(e) => {
              setSpecifyCarrier(e.target.checked);
              if (!e.target.checked) {
                setSelectedCarriers([]);
              }
            }}
            style={{ marginRight: '0.5rem', cursor: 'pointer' }}
          />
          Specify Carrier
        </label>
        
        {specifyCarrier && (
          <div style={{ marginTop: '0.5rem', marginLeft: '1.5rem', display: 'flex', flexDirection: 'column', gap: '0.15rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '0.875rem' }}>
              <input
                type="checkbox"
                checked={selectedCarriers.includes('ups')}
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelectedCarriers([...selectedCarriers, 'ups']);
                  } else {
                    setSelectedCarriers(selectedCarriers.filter(c => c !== 'ups'));
                  }
                }}
                style={{ marginRight: '0.5rem', cursor: 'pointer' }}
              />
              UPS
            </label>
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '0.875rem' }}>
              <input
                type="checkbox"
                checked={selectedCarriers.includes('usps')}
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelectedCarriers([...selectedCarriers, 'usps']);
                  } else {
                    setSelectedCarriers(selectedCarriers.filter(c => c !== 'usps'));
                  }
                }}
                style={{ marginRight: '0.5rem', cursor: 'pointer' }}
              />
              USPS
            </label>
          </div>
        )}
        <div style={helperStyle}>
          {specifyCarrier 
            ? 'Select carriers for analysis'
            : 'Uses consolidated carrier data'
          }
        </div>
      </div>

      <div style={{ marginBottom: '1rem' }}>
        <label style={labelStyle}>
          Existing Warehouses
        </label>
        <input
          type="text"
          value={requiredInput}
          onChange={(e) => setRequiredInput(e.target.value)}
          placeholder="e.g., 900, 606, 100"
          style={wideInputStyle}
        />
        <div style={helperStyle}>Include these required locations</div>
      </div>

      {/* Zip3 Constraints */}
      <div style={{ ...sectionHeaderStyle, marginTop: '1rem' }}>
        Zip3 Constraints
      </div>

      <div style={{ marginBottom: '.5rem' }}>
        <label style={labelStyle}>
          Include Zip3s
        </label>
        <input
          type="text"
          value={candidatesInput}
          onChange={(e) => setCandidatesInput(e.target.value)}
          placeholder="Blank for all locations"
          style={wideInputStyle}
        />
        <div style={helperStyle}>Leave blank to include all ZIP3s</div>
      </div>

      <div style={{ marginBottom: '1rem' }}>
        <label style={labelStyle}>
          Excluded ZIP3s
        </label>
        <input
          type="text"
          value={excludedZip3sInput}
          onChange={(e) => setExcludedZip3sInput(e.target.value)}
          placeholder="e.g., 995, 996, 997"
          style={mediumInputStyle}
        />
        <div style={helperStyle}>Never search these ZIP3s</div>
      </div>

      {/* State Constraints */}
      <div style={{ ...sectionHeaderStyle, marginTop: '1rem' }}>
        State Constraints
      </div>

      <div style={{ marginBottom: '.5rem' }}>
        <label style={labelStyle}>
          Include States
        </label>
        <input
          type="text"
          value={includedStatesInput}
          onChange={(e) => setIncludedStatesInput(e.target.value)}
          placeholder="e.g., CA, TX, FL, NY"
          style={wideInputStyle}
        />
        <div style={helperStyle}>Leave blank to include all states</div>
      </div>

      <div style={{ marginBottom: '1.5rem' }}>
        <label style={labelStyle}>
          Exclude States
        </label>
        <input
          type="text"
          value={excludedStatesInput}
          onChange={(e) => setExcludedStatesInput(e.target.value)}
          placeholder="e.g., AK, HI"
          style={mediumInputStyle}
        />
        <div style={helperStyle}>Never search in these states</div>
      </div>

      {/* Submit Button */}
      <button
        type="submit"
        disabled={isLoading || !isFormValid}
        style={{
          width: '100%',
          padding: '0.75rem 1rem',
          backgroundColor: (isLoading || !isFormValid) ? '#9ca3af' : 'var(--primary)',
          color: '#fff',
          border: 'none',
          borderRadius: '6px',
          fontSize: '1rem',
          fontWeight: 600,
          cursor: (isLoading || !isFormValid) ? 'not-allowed' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.5rem',
          transition: 'all 0.2s',
        }}
      >
        {isLoading ? (
          <>
            <div style={{
              width: '16px',
              height: '16px',
              border: '2px solid var(--text-secondary)',
              borderTopColor: 'transparent',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
            }} />
            Optimizing...
          </>
        ) : (
          <>
            <Warehouse size={18} />
            Optimize Coverage
          </>
        )}
      </button>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </form>
  );
}

